import { SpinnerGapIcon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Primitivas de la dirección «Editorial Pop».
 *
 * Son cuatro piezas y con ellas se arma cualquier pantalla: el bloque de tinta
 * que abre, la tarjeta tintada que agrupa, el rótulo numerado que ordena y la
 * pastilla negra que actúa. Estaban repetidas a mano en cada ruta con medidas
 * ligeramente distintas —radios de 14, 16 y 20 px para la misma clase de caja—,
 * y esa deriva es lo que hacía que la app pareciera varias apps.
 */

/* ── Tintes ──────────────────────────────────────────────────────────────── */

/**
 * De qué habla un bloque, dicho en color antes de leer el título.
 *
 * El reparto es semántico y no estético: `butter` es lo que entra, `peach` lo
 * que sale, `mint` el ahorro y `lavender` las tarjetas. Elegir el tinte «porque
 * se ve bien» rompe la única razón por la que sirven.
 */
export type Tinte = 'butter' | 'lavender' | 'mint' | 'peach' | 'neutro'

const FONDO: Record<Tinte, string> = {
  butter: 'bg-tint-butter',
  lavender: 'bg-tint-lavender',
  mint: 'bg-tint-mint',
  peach: 'bg-tint-peach',
  neutro: 'bg-surface',
}

const SELLO: Record<Tinte, string> = {
  butter: 'bg-tint-butter-solid text-on-brand',
  lavender: 'bg-tint-lavender-solid text-on-brand',
  mint: 'bg-tint-mint-solid text-on-brand',
  peach: 'bg-tint-peach-solid text-on-brand',
  neutro: 'bg-surface-sunken text-ink-soft',
}

/* ── Bloque de tinta ─────────────────────────────────────────────────────── */

/**
 * El bloque que abre la pantalla.
 *
 * Va **a sangre** por los tres lados y solo redondea abajo. Pegarlo a los
 * bordes es lo que lo convierte en un bloque editorial; con margen lateral
 * sería una tarjeta oscura más, y la pantalla volvería a leerse como una lista
 * de tarjetas sin jerarquía.
 *
 * Los márgenes negativos cancelan el `px-safe` de la página: así el bloque
 * llega al borde sin que cada pantalla tenga que salirse de su propio contenedor
 * a mano.
 */
export function BloqueTinta({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        // El `-mt` sube el bloque hasta el borde superior, por detrás de la
        // barra de estado, que es donde tiene que morir el color.
        '-mx-[max(env(safe-area-inset-left),1rem)] -mt-safe',
        'relative overflow-hidden rounded-b-hero bg-navy px-[max(env(safe-area-inset-left),1.25rem)]',
        'pb-5 pt-safe text-on-navy',
        className,
      )}
    >
      {/* Dos círculos de tinta clara, sin degradado.
          Un degradado sobre el bloque lo volvería «fondo de app de banco»; dos
          formas planas recortadas por el borde dan profundidad sin salirse de
          la línea plana de la referencia audaz. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-10 size-44 rounded-full bg-navy-soft"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -left-12 bottom-0 size-28 rounded-full bg-navy-soft"
      />
      <div className="relative">{children}</div>
    </div>
  )
}

/* ── Rótulo de sección ───────────────────────────────────────────────────── */

/**
 * Título de sección con su número de orden.
 *
 * El número en versalita mono no es adorno: da a la pantalla un sentido de
 * secuencia —`01`, `02`, `03`— que una pila de títulos iguales no tiene, y es
 * lo que permite saber «voy por la mitad» sin barra de progreso.
 */
export function Rotulo({
  numero,
  titulo,
  accion,
  className,
}: {
  numero?: string
  titulo: string
  accion?: ReactNode
  className?: string
}) {
  return (
    <header className={cn('mb-2.5 flex items-baseline gap-2 px-1', className)}>
      {numero ? (
        <span className="font-mono text-[10px] font-bold text-ink-faint">{numero}</span>
      ) : null}
      <h2 className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-ink">
        {titulo}
      </h2>
      {accion ? <div className="ml-auto">{accion}</div> : null}
    </header>
  )
}

/* ── Tarjeta tintada ─────────────────────────────────────────────────────── */

/** Caja de sección. Sin borde ni sombra: el tinte ya la separa del papel. */
export function TarjetaTinte({
  tinte = 'neutro',
  children,
  className,
  as: Tag = 'section',
}: {
  tinte?: Tinte
  children: ReactNode
  className?: string
  as?: 'section' | 'div' | 'article' | 'ul'
}) {
  return (
    <Tag
      className={cn(
        'rounded-hero p-4',
        FONDO[tinte],
        // El tinte neutro es el único que necesita línea: sobre papel marfil, el
        // marfil de la superficie no se separa solo.
        tinte === 'neutro' && 'border border-line',
        className,
      )}
    >
      {children}
    </Tag>
  )
}

/* ── Pastilla de acción ──────────────────────────────────────────────────── */

/**
 * El botón del sistema: pastilla de tinta con chevrón doble en mantequilla.
 *
 * Negra y no índigo, y esa es la decisión que sostiene toda la paleta: el índigo
 * pasa a ser el color del **dato** —el tramo libre de la dona, un enlace—, así
 * que si también vistiera los botones, el dato parecería pulsable y el botón un
 * dato. El chevrón va en mantequilla porque es el único acento de marca y un
 * botón es el mejor sitio para gastarlo.
 */
export function Pastilla({
  children,
  onClick,
  type = 'button',
  variante = 'tinta',
  disabled = false,
  ocupado = false,
  icono,
  conChevron = true,
  className,
}: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  variante?: 'tinta' | 'contorno' | 'marca'
  disabled?: boolean
  /** Mientras se guarda: girador en lugar del texto y sin aceptar otro toque. */
  ocupado?: boolean
  /** Antes del texto. El chevrón va siempre al final, así que no hace falta flecha. */
  icono?: ReactNode
  conChevron?: boolean
  className?: string
}) {
  const inactiva = disabled || ocupado
  return (
    <button
      type={type}
      disabled={inactiva}
      aria-busy={ocupado || undefined}
      onClick={
        onClick
          ? () => {
              haptic('light')
              onClick()
            }
          : undefined
      }
      className={cn(
        'flex min-h-[52px] w-full items-center justify-center gap-2 rounded-pill px-6',
        'text-[15px] font-bold transition-transform duration-150',
        'active:scale-[0.97] disabled:opacity-60',
        // `accion` y no `navy`: en oscuro el bloque y el botón ya no son la
        // misma tinta (ver la ficha en tokens.css).
        variante === 'tinta' && 'bg-accion text-on-accion',
        variante === 'marca' && 'bg-brand text-on-brand',
        variante === 'contorno' && 'border border-line-strong bg-surface text-ink',
        className,
      )}
    >
      {ocupado ? (
        <SpinnerGapIcon size={19} className="animate-spin" />
      ) : (
        <>
          {icono}
          <span>{children}</span>
          {/* Se apaga con el botón: un acento vivo sobre un botón muerto se
              lee como que todavía se puede tocar. */}
          {conChevron && !disabled ? (
            <span
              aria-hidden="true"
              className={cn(
                'text-[17px] font-black leading-none',
                variante === 'tinta' ? 'text-accion-acento' : 'text-current',
              )}
            >
              »
            </span>
          ) : null}
        </>
      )}
    </button>
  )
}

/* ── Fila de agenda ──────────────────────────────────────────────────────── */

/**
 * Un renglón de «Lo que viene»: sello de día, qué es, de dónde sale y cuánto.
 *
 * El sello con el número de día a la izquierda es lo que hace legible una lista
 * mezclada de nóminas, fijos, mensualidades y cortes: la fecha es lo único que
 * todos comparten, así que es la columna que permite leerla en diagonal. Antes
 * esas cuatro cosas vivían en cuatro bloques distintos y ninguno decía en qué
 * orden iban a pasar.
 */
export function FilaAgendaEditorial({
  dia,
  mes,
  tinte = 'neutro',
  icono,
  titulo,
  detalle,
  monto,
  onClick,
}: {
  dia: string
  mes?: string
  tinte?: Tinte
  /**
   * Antes del título, para marcar lo urgente —un pago vencido— sin cambiar el
   * tinte de la fila: el tinte dice QUÉ es, y si también dijera «urgente»
   * perdería la única razón por la que sirve.
   */
  icono?: ReactNode
  titulo: string
  detalle: string
  monto: ReactNode
  onClick?: () => void
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      {...(onClick
        ? {
            type: 'button' as const,
            onClick: () => {
              haptic('light')
              onClick()
            },
          }
        : {})}
      className={cn(
        'flex w-full items-center gap-3 rounded-card px-3 py-2.5 text-left',
        FONDO[tinte],
        tinte === 'neutro' && 'border border-line',
        onClick && 'active:scale-[0.99]',
      )}
    >
      {/* 44 px y el mes a 9 px. Estaba a 40 px con el mes a 7.5 px y al 70 %
          de opacidad: un rótulo que en un teléfono ya no se lee no fecha nada,
          y la fecha es justo la columna por la que se recorre esta lista. */}
      <span
        className={cn(
          'flex size-11 shrink-0 flex-col items-center justify-center rounded-chip leading-none',
          SELLO[tinte],
        )}
      >
        <span className="text-[15px] font-extrabold tabular-nums">{dia}</span>
        {mes ? (
          <span className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.08em]">{mes}</span>
        ) : null}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-[14px] font-semibold text-ink">
          {icono ? <span className="shrink-0">{icono}</span> : null}
          <span className="truncate">{titulo}</span>
        </span>
        <span className="block truncate text-[12px] text-ink-soft">{detalle}</span>
      </span>

      <span className="shrink-0 text-right">{monto}</span>
    </Tag>
  )
}
